import { Component, computed, inject, input, output, signal } from '@angular/core'
import { RouterModule } from '@angular/router'
import {
  LucideLock, LucideLogOut, LucideUserRound, LucideCamera,
  LucideMoon, LucideContrast, LucideCloud, LucideSun, LucideBell, LucideMessageCircle,
} from '@lucide/angular'
import { User } from '../../interfaces/user.interface'
import { ThemeService, ThemeId, THEMES } from '../../services/theme.service'
import { AvatarComponent } from '../avatar/avatar.component'
import { PhotoEditorComponent } from './photo-editor.component'
import { roleLabel } from './launcher-items'

/**
 * The account card behind the avatar (CC 04/10/2026, Google's account card as the model):
 * who is signed in, their own pages, the colour theme and Sign out. Admin tools moved to
 * the launcher, so this is the same for every role.
 *
 * Sized against Google / Microsoft 365 (CC 05/10/2026: it was too big). On a phone (`compact`)
 * the greeting block becomes one row and Manage my profile joins the links, and on every
 * screen Sign out is pinned to the bottom edge, so it never needs a scroll.
 */
@Component({
  selector: 'app-account-panel',
  standalone: true,
  imports: [
    RouterModule, AvatarComponent, PhotoEditorComponent,
    LucideLock, LucideLogOut, LucideUserRound, LucideCamera,
    LucideMoon, LucideContrast, LucideCloud, LucideSun, LucideBell, LucideMessageCircle,
  ],
  template: `
    @if (view() === 'photo') {
      <app-photo-editor [user]="user()" (closed)="view.set('main')"></app-photo-editor>
    } @else {
    @if (compact()) {
      <section class="acard awho">
        <button type="button" class="acard__ring" aria-label="Change your photo" (click)="view.set('photo')">
          <app-avatar [entity]="user()" [size]="44"></app-avatar>
          <span class="acam" aria-hidden="true"><svg lucideCamera [size]="12"></svg></span>
        </button>
        <div class="awho__text">
          <div class="awho__name">{{ fullName() }}</div>
          <div class="acard__email">{{ user().email }}</div>
        </div>
        <span class="acard__role">{{ role() }}</span>
      </section>
    } @else {
      <section class="acard acard--who">
        <div class="acard__email">{{ user().email }}</div>
        <button type="button" class="acard__ring" aria-label="Change your photo" (click)="view.set('photo')">
          <app-avatar [entity]="user()" [size]="64"></app-avatar>
          <span class="acam" aria-hidden="true"><svg lucideCamera [size]="14"></svg></span>
        </button>
        <div class="acard__hello">Hi, {{ user().first_name }}!</div>
        <span class="acard__role">{{ role() }}</span>
        <a class="acard__manage" routerLink="/profile" [replaceUrl]="true"
           routerLinkActive #rlaProfile="routerLinkActive" [routerLinkActiveOptions]="{ exact: true }"
           (click)="picked.emit({ navigating: !rlaProfile.isActive })">Manage my profile</a>
      </section>
    }

    <section class="acard acard--links">
      @if (compact()) {
        <a class="arow" routerLink="/profile" [replaceUrl]="true"
           routerLinkActive #rlaProfileRow="routerLinkActive" [routerLinkActiveOptions]="{ exact: true }"
           (click)="picked.emit({ navigating: !rlaProfileRow.isActive })">
          <svg lucideUserRound [size]="20" class="arow__icon arow__icon--accent"></svg>
          Manage my profile
        </a>
      }
      <a class="arow" routerLink="/settings/notifications" [replaceUrl]="true"
         routerLinkActive #rlaNotify="routerLinkActive" [routerLinkActiveOptions]="{ exact: true }"
         (click)="picked.emit({ navigating: !rlaNotify.isActive })">
        <svg lucideBell [size]="20" class="arow__icon"></svg>
        My notifications
      </a>
      <a class="arow" routerLink="/profile/change-password" [replaceUrl]="true"
         routerLinkActive #rlaPassword="routerLinkActive" [routerLinkActiveOptions]="{ exact: true }"
         (click)="picked.emit({ navigating: !rlaPassword.isActive })">
        <svg lucideLock [size]="20" class="arow__icon"></svg>
        Change password
      </a>
      <!-- Phones under 400px: the top bar's WhatsApp button lives here, so the name beside the
           logo has room (Release 5, 31/10/2026, SHELL-02). -->
      @if (whatsAppHref(); as href) {
        <a class="arow" [href]="href" target="_blank" rel="noopener" data-testid="account-whatsapp"
           (click)="picked.emit({ navigating: false })">
          <svg lucideMessageCircle [size]="20" class="arow__icon arow__icon--wa"></svg>
          Ask us on WhatsApp
        </a>
      }
    </section>

    <section class="acard">
      <h2 class="acard__label" id="appearance-label">Appearance</h2>
      <div class="athemes" role="group" aria-labelledby="appearance-label">
        @for (option of themes; track option.id) {
          <button type="button"
                  class="theme-btn"
                  [class.theme-btn--active]="theme() === option.id"
                  [attr.aria-pressed]="theme() === option.id"
                  [title]="option.label"
                  (click)="setTheme(option.id)">
            @switch (option.icon) {
              @case ('moon') { <svg lucideMoon [size]="16"></svg> }
              @case ('contrast') { <svg lucideContrast [size]="16"></svg> }
              @case ('cloud') { <svg lucideCloud [size]="16"></svg> }
              @case ('sun') { <svg lucideSun [size]="16"></svg> }
            }
            <span>{{ option.label }}</span>
          </button>
        }
      </div>
    </section>

    <!-- Pinned to the bottom of the panel: always in sight, however short the screen. -->
    <div class="afoot" [class.afoot--sheet]="compact()">
      <button type="button" class="asignout" (click)="signOut.emit()">
        <svg lucideLogOut [size]="18"></svg>
        Sign out
      </button>
    </div>
    }
  `,
  styles: [`
    :host { display: flex; flex-direction: column; gap: 10px; }

    .acard {
      border-radius: 22px;
      padding: 14px;
      background: var(--scios-surface-2);
    }
    .acard--who {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      padding: 14px 16px;
      text-align: center;
    }
    /* Phone: who is signed in, on one row. */
    .awho {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 14px;
    }
    .awho__text { flex: 1; min-width: 0; }
    .awho__name {
      font-size: 16px;
      font-weight: 600;
      color: var(--scios-text-primary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .awho .acard__role { flex-shrink: 0; }
    .acard__email {
      max-width: 100%;
      font-size: 13px;
      color: var(--scios-text-secondary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    /* The brand ring around the avatar: cyan through violet to orange. */
    .acard__ring {
      position: relative;
      display: flex;
      flex-shrink: 0;
      padding: 3px;
      border: none;
      border-radius: 50%;
      background: conic-gradient(#06D6F5, #8B5CF6, #F97316, #06D6F5);
      cursor: pointer;
    }
    .acard__ring:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 3px; }
    /* Camera badge: the avatar is the button that changes the photo. */
    .acam {
      position: absolute;
      right: -2px;
      bottom: -2px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: var(--accent-primary);
      color: #04111d;
      box-shadow: 0 0 0 2px var(--scios-surface-2);
    }
    .awho .acam { width: 20px; height: 20px; }
    /* The 50px phone avatar still gets a 44px+ tap area. */
    .awho .acard__ring::after { content: ''; position: absolute; inset: -4px; }
    .acard__ring app-avatar {
      display: block;
      border-radius: 50%;
      box-shadow: 0 0 0 3px var(--scios-surface-2);
    }
    .acard__hello {
      font-size: 18px;
      font-weight: 600;
      color: var(--scios-text-primary);
    }
    .acard__role {
      font-size: 12px;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 10px;
      color: var(--accent-primary);
      background: var(--accent-primary-15);
    }
    .acard__manage {
      display: inline-flex;
      align-items: center;
      min-height: 44px;
      padding: 0 20px;
      border-radius: 22px;
      border: 1px solid var(--scios-border-hover, var(--scios-border));
      color: var(--accent-primary);
      font-size: 14px;
      font-weight: 600;
      text-decoration: none;
    }

    .acard--links { display: flex; flex-direction: column; padding: 4px 0; overflow: hidden; }
    .arow {
      display: flex;
      align-items: center;
      gap: 14px;
      min-height: 48px;
      padding: 0 18px;
      font-size: 14px;
      color: var(--scios-text-primary);
      text-decoration: none;
    }
    .arow + .arow { border-top: 1px solid var(--scios-border-subtle, var(--scios-border)); }
    .arow__icon { color: var(--scios-text-secondary); flex-shrink: 0; }
    .arow__icon--purple { color: #A78BFA; }
    .arow__icon--accent { color: var(--accent-primary); }
    .arow__icon--wa { color: #25D366; }
    :host-context(html[data-theme="light"]) .arow__icon--wa { color: #0B7A3E; }

    .acard__label {
      margin: 0 0 8px;
      font-size: 13px;
      font-weight: 600;
      color: var(--scios-text-secondary);
    }
    .athemes {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 6px;
    }
    /* One 44px row: icon beside the name. */
    .theme-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      min-height: 44px;
      padding: 0 4px;
      border-radius: 12px;
      border: 1px solid var(--scios-border);
      background: transparent;
      color: var(--scios-text-secondary);
      font: inherit;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
    }
    .theme-btn--active,
    .theme-btn--active:hover {
      border-color: var(--accent-primary);
      background: var(--accent-primary-15);
      color: var(--accent-primary);
    }

    /* Sticky inside the panel's scroll area; it paints the panel colour so cards scrolling
       under it stay hidden. The sheet version keeps clear of the iPhone home bar. */
    .afoot {
      position: sticky;
      bottom: 0;
      z-index: 1;
      margin: -2px -12px 0;
      padding: 2px 12px 12px;
      background: var(--scios-surface-1);
    }
    .afoot--sheet { padding-bottom: calc(12px + env(safe-area-inset-bottom)); }
    .asignout {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      min-height: 48px;
      border: none;
      border-radius: 22px;
      background: var(--scios-surface-2);
      color: var(--scios-text-primary);
      font: inherit;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
    }

    @media (hover: hover) {
      .arow:hover, .asignout:hover, .acard__manage:hover {
        background: color-mix(in srgb, var(--scios-text-primary) 7%, transparent);
      }
      .theme-btn:hover { color: var(--scios-text-primary); }
    }
    .arow:focus-visible, .asignout:focus-visible, .acard__manage:focus-visible, .theme-btn:focus-visible {
      outline: 2px solid var(--accent-primary);
      outline-offset: -2px;
    }
  `],
})
export class AccountPanelComponent {
  private readonly themeService = inject(ThemeService)

  readonly user = input.required<User>()
  /** Phone bottom sheet: the shorter layout. */
  readonly compact = input(false)
  /** "Ask us on WhatsApp" as a row: set by the top bar below 400px wide, null otherwise. */
  readonly whatsAppHref = input<string | null>(null)

  /** The card, or Change photo in its place. */
  protected readonly view = signal<'main' | 'photo'>('main')
  readonly picked = output<{ navigating: boolean }>()
  readonly signOut = output<void>()

  protected readonly themes = THEMES
  protected readonly theme = this.themeService.theme
  protected readonly role = computed(() => roleLabel(this.user().role))
  protected readonly fullName = computed(() =>
    [this.user().first_name, this.user().last_name].filter(Boolean).join(' ') || this.user().email)

  setTheme (id: ThemeId): void {
    this.themeService.setTheme(id)
  }
}
